import { useMemo } from 'react'
import { Spinner } from '../../../components/atoms/Spinner'
import { CheckboxList } from './CheckboxList'
import { PriceSlider } from './PriceSlider'
import { useShopFilterMeta } from '../../../../viewmodels/useShopFilterMeta'
import { useShopFilterAttributes } from '../../../../viewmodels/useShopFilterAttributes'
import { useShopPriceRange } from '../../../../viewmodels/useShopPriceRange'
import type { ShopProductsFilter } from '../../../../viewmodels/useShopProducts'

/**
 * ShopFilterPanel — left sidebar for the shop list. Three sections:
 *   1. Price (slider — always visible; bounds seeded from catalog)
 *   2. Category (scrollable multi-select checkbox list)
 *   3. Per-attribute CDT lists — APPEAR ONLY when at least one
 *      category is selected (per Kiran's spec: "once category is
 *      selected then it's custom type need to be selected").
 *
 * Reads two viewmodels (filter-meta + filter-attributes). Pushes
 * filter changes back to the parent via onFiltersChange — the parent
 * owns the canonical filter state + the URL sync.
 */
interface Props {
  readonly filter: ShopProductsFilter
  readonly onFiltersChange: (next: ShopProductsFilter) => void
}

export function ShopFilterPanel({ filter, onFiltersChange }: Props): JSX.Element {
  const meta = useShopFilterMeta()
  const attrs = useShopFilterAttributes(filter.categoryIds)
  // DYNAMIC slider bounds: refetched whenever the selected category
  // set changes. While the refetch is in flight (and as a fallback on
  // error) we keep showing the previous min/max from the meta seed.
  const dynamicRange = useShopPriceRange(filter.categoryIds)
  const selectedCategoryIds = useMemo(() => new Set(filter.categoryIds), [filter.categoryIds])

  const liveMin = dynamicRange.priceRange.min_paisa ?? meta.priceRange.min_paisa
  const liveMax = dynamicRange.priceRange.max_paisa ?? meta.priceRange.max_paisa
  const hasRange = liveMin !== null && liveMax !== null && liveMax > liveMin
  const minBound = liveMin ?? 0
  const maxBound = liveMax ?? 1
  const valueMin = filter.priceMinPaisa ?? minBound
  const valueMax = filter.priceMaxPaisa ?? maxBound

  function toggleCategory(id: string): void {
    const exists = selectedCategoryIds.has(id)
    const next = exists
      ? filter.categoryIds.filter((c) => c !== id)
      : [...filter.categoryIds, id]
    // Drop attribute filters for attributes whose values no longer
    // belong to any selected category — handled by the server, but
    // we also clean local state so the next refetch doesn't carry
    // stale ids.
    onFiltersChange({ ...filter, categoryIds: next })
  }

  function toggleAttributeValue(attrId: string, valueId: string): void {
    const current = filter.attributeValues[attrId] ?? []
    const exists = current.includes(valueId)
    const nextValues = exists
      ? current.filter((v) => v !== valueId)
      : [...current, valueId]
    const nextAttrs = { ...filter.attributeValues }
    if (nextValues.length === 0) delete nextAttrs[attrId]
    else nextAttrs[attrId] = nextValues
    onFiltersChange({ ...filter, attributeValues: nextAttrs })
  }

  function commitPrice(next: { minPaisa: number; maxPaisa: number }): void {
    onFiltersChange({
      ...filter,
      priceMinPaisa: next.minPaisa === minBound ? null : next.minPaisa,
      priceMaxPaisa: next.maxPaisa === maxBound ? null : next.maxPaisa
    })
  }

  // Lives INSIDE the walnut filter drawer (matches OperatorSidebar
  // chrome). Sections sit on a faint light-on-dark wash so the rounded
  // cards read against the bg-dark→charcoal gradient.
  const sectionClass = 'rounded-xl border border-text-light/15 bg-text-light/5 p-4 shadow-sm'
  const headingClass = 'mb-3 font-heading text-h4 text-text-light'
  const mutedClass = 'font-body text-body-xs text-text-light/70'

  return (
    <aside className="w-full space-y-5">
      {/* Price — dynamic bounds (scoped to selected categories when any) */}
      <section className={sectionClass}>
        <h3 className={headingClass}>
          Price
          {filter.categoryIds.length > 0 ? (
            <span className={`ml-2 ${mutedClass}`}>
              (in selected categories)
            </span>
          ) : null}
        </h3>
        {meta.isLoading && !hasRange ? (
          <div className="flex items-center justify-center py-3"><Spinner /></div>
        ) : !hasRange ? (
          <p className={mutedClass}>
            {filter.categoryIds.length > 0
              ? 'No priced products in the selected categories.'
              : 'No priced products yet.'}
          </p>
        ) : (
          <PriceSlider
            catalogMinPaisa={minBound}
            catalogMaxPaisa={maxBound}
            valueMinPaisa={valueMin}
            valueMaxPaisa={valueMax}
            onCommit={commitPrice}
            disabled={dynamicRange.isLoading}
          />
        )}
      </section>

      {/* Category */}
      <section className={sectionClass}>
        <h3 className={headingClass}>Category</h3>
        {meta.isLoading ? (
          <div className="flex items-center justify-center py-3"><Spinner /></div>
        ) : (
          <CheckboxList
            options={meta.categories.map((c) => ({ id: c.id, label: c.name, subLabel: c.product_count }))}
            selectedIds={selectedCategoryIds}
            onToggle={toggleCategory}
            emptyNote="No categories yet."
          />
        )}
      </section>

      {/* Per-attribute CDT lists — only shown when a category is selected. */}
      {filter.categoryIds.length > 0 ? (
        attrs.isLoading ? (
          <div className={sectionClass}>
            <div className="flex items-center justify-center py-3"><Spinner /></div>
          </div>
        ) : attrs.groups.length === 0 ? null : (
          attrs.groups.map((g) => {
            const selected = new Set(filter.attributeValues[g.attribute_id] ?? [])
            return (
              <section key={g.attribute_id} className={sectionClass}>
                <h3 className={headingClass}>{g.attribute_label}</h3>
                <CheckboxList
                  options={g.values.map((v) => ({ id: v.id, label: v.label }))}
                  selectedIds={selected}
                  onToggle={(vid) => toggleAttributeValue(g.attribute_id, vid)}
                />
              </section>
            )
          })
        )
      ) : null}
    </aside>
  )
}
